import Decimal from 'decimal.js';

import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getDiscountedValue } from '@/utils/getDiscountedValue';

import {
  FundingKind,
  FundingPattern,
} from './estateWaterfallHypotheticalTransfers.types';

/**
 * Computes the effective transfer amount based on the selected funding kind
 * and pattern. Returns null when the inputs are insufficient to compute a value.
 */
export function computeTransferAmount({
  fundingKind,
  proRataPattern,
  proRataAmount,
  proRataPercent,
  totalSourceValue,
  totalAssetsValue,
}: {
  fundingKind: FundingKind | null;
  proRataPattern: FundingPattern;
  proRataAmount: Decimal | null | undefined;
  proRataPercent: Decimal | null | undefined;
  totalSourceValue: Decimal | null | undefined;
  totalAssetsValue: Decimal | null;
}): Decimal | null {
  if (fundingKind === 'proRata') {
    if (proRataPattern === 'amount') {
      return proRataAmount ?? null;
    } else if (proRataPercent && totalSourceValue) {
      return totalSourceValue.times(proRataPercent).dividedBy(100);
    }
  } else if (fundingKind === 'assets') {
    return totalAssetsValue;
  }
  return null;
}

/**
 * Generates the help text string that shows the discounted transfer value
 * for estate tax purposes. Returns undefined when inputs are insufficient.
 */
export function getTransferDiscountHelpText({
  transferAmount,
  discountRate,
}: {
  transferAmount: Decimal | null;
  discountRate: Decimal | null | undefined;
}): string | undefined {
  if (!transferAmount || !discountRate || discountRate.eq(0)) return undefined;

  const discountedValue = getDiscountedValue(transferAmount, discountRate);
  if (!discountedValue) return undefined;

  return `Transfer amount equals ${formatCurrencyNoDecimals(discountedValue)} for estate tax purposes`;
}
